// @flow
import React, {Component} from 'react';
import {compose} from 'redux';
import {connect} from 'react-redux';
import BackToTopButton from '../BackToTopButton/BackToTopButton';
import ExpandedGroups from '../ExpandedGroups/ExpandedGroups';
import {ErrorReport} from '@homeaway/react-error-reporter';
import Oops from '../ErrorBoundary/FallbackComponent';
import {OOPS_ERROR_NAME} from '../ErrorBoundary/ErrorBoundary';
import {HitCarouselIconDefinitions} from '../HitCarouselContainer/HitCarouselContainer';
import {filter as filterGraphQlAnywhere} from 'graphql-anywhere';
import HitCollection, {
    fragments as hitCollectionFragments
} from '../HitCollection/HitCollection';
import Pager from '../Pager/Pager';
import MetaBar from '../MetaBar/MetaBar';
import DisclaimerMessaging from '../DisclaimerMessaging/DisclaimerMessaging';
import ResultsLoading from '../ResultsLoading/ResultsLoading';
import type {Shape as PagerShape} from '../Pager/Pager';
import ConnectedSkipToContentAnchor from '../ConnectedSkipToContent/ConnectedSkipToContentAnchor';
import displayActions from '../../actions/display';
import mapActions from '../../actions/map';
import NoResults from '../NoResults/NoResults';
import type {Shape as ResultsCountShape} from '../ResultsCount/ResultsCount';
import {trigger as edapTrigger, GENERIC_EVENT} from '../../utils/edapWrapper';
import {scrollToAnimated} from '../../utils/scrollTo';
import {media} from '../../utils/breakpoints';
import {
    triggerSearchResultPresentedEDAPEvent,
    triggerDeepestSearchResultPresentedEDAPEvent
} from '../../utils/hitEdapUtils';
import {checkedFilters} from '../../../server/utils/filters';
import searchParamsActions from '../../actions/searchParams';
import {isEqual} from 'lodash';

import type {MouseLeaveShape} from '../Hit/Hit';
import type {Breakpoint} from '../../utils/breakpoints';
import type {State as globalState} from '../../reducers';
import type {SearchParamsType} from '../../reducers/searchParams';
import type {Action as MobileScrollerAction} from '../../actions/display';
import {Unveil} from '@homeaway/react-unveil';

import './Results.less';

type StateProps = {
    breakpoint: Breakpoint,
    abacusExperiments: Object,
    numFilters?: number,
    isDesktop: boolean
};

type ServiceRequest = {
    serviceName: string,
    url: string
};

type GraphQLError = {
    message: string,
    extensions?: {
        code: string,
        request?: ServiceRequest
    }
};

type ApolloError = {
    graphQLErrors: Array<GraphQLError>
};

type Props = {
    loading: boolean,
    results: ?Object,
    error: ?ApolloError,
    allowUpdate: boolean,
    showMobileScroller: () => MobileScrollerAction,
    hideMobileScroller: () => MobileScrollerAction,
    syncInputParams: (?SearchParamsType) => any,
    highlightPin: (string) => any,
    clearHighlightedPins: () => any
} & StateProps;

type ScrolledState = {
    '0': number,
    '5': number,
    '10': number
};

type State = {
    scrolled: ScrolledState,
    scrollStopPosition: number,
    scrollVelocity: number,
    timeOverListing: number,
    currentState?: number,
    hasEvaluated: boolean,
    loggedFirstHitHover: boolean
};

const defaultScrolledState = {
    '0': -1,
    '5': -1,
    '10': -1
};

export class Results extends Component<Props, State> {
    constructor(props: Props) {
        super(props);

        this.state = {
            loggedFirstHitHover: false,
            scrolled: defaultScrolledState,
            hasEvaluated: false,
            scrollVelocity: 0,
            scrollStopPosition: 0,
            timeOverListing: 0
        };
    }

    componentDidMount() {
        const {loading, results} = this.props;
        if (!loading && results && results.listings) {
            // this is needed for server side rendering because when the component is mounted, the results are already available and hence
            // componentWillReceiveProps is not called
            const checked = checkedFilters(results.filterGroups);
            this.props.syncInputParams({
                filter: checked
            });
        }
        window.addEventListener(
            'beforeunload',
            triggerDeepestSearchResultPresentedEDAPEvent
        );
    }

    // fired for client side searches
    componentWillReceiveProps(nextProps: Props) {
        if (!nextProps.loading) {
            this.updateSelectedFilters(nextProps);
        } else {
            this.props.hideMobileScroller();
        }
    }

    shouldComponentUpdate(nextProps: Props) {
        return nextProps.allowUpdate;
    }

    updateSelectedFilters(nextProps: Props) {
        const nextFilterGroups = nextProps.results
            ? nextProps.results.filterGroups
            : null;
        const filterGroups = this.props.results
            ? this.props.results.filterGroups
            : null;

        if (nextFilterGroups && !isEqual(filterGroups, nextFilterGroups)) {
            this.props.syncInputParams({
                filter: checkedFilters(nextFilterGroups)
            });
        }
    }

    handleScrollHitEnter = (label: number) => {
        const hitEntered = this.state.scrolled[label];
        const newPosition = window.scrollY;
        const currentScrollTop =
            (window && newPosition) ||
            (document &&
                document.documentElement &&
                document.documentElement.scrollTop) ||
            0;
        this.setState({
            scrollVelocity: newPosition - this.state.scrollStopPosition,
            scrollStopPosition: newPosition
        });
        if (hitEntered < 0) {
            edapTrigger(GENERIC_EVENT, {
                eventaction: 'page scroll',
                eventlabel: label,
                highfrequency: 'true'
            });
            this.setState({
                scrolled: {
                    ...this.state.scrolled,
                    [label]: currentScrollTop
                }
            });
        }
        this.checkForMobileScroller(currentScrollTop);
    };

    onHitMouseEnter = (listingId: string) => {
        this.handleFirstHitHover();
        this.props.highlightPin(listingId);
        this.setState({
            timeOverListing: Date.now()
        });
    };

    onHitMouseLeave = (mouseLeaveData: MouseLeaveShape) => {
        this.props.clearHighlightedPins();
        if (this.props.isDesktop) {
            const timeOverListing = Math.floor(
                (Date.now() - this.state.timeOverListing) / 1000
            );

            if (timeOverListing >= 2) {
                triggerSearchResultPresentedEDAPEvent({
                    ...mouseLeaveData,
                    timeOverListing,
                    scrollVelocity: this.state.scrollVelocity
                });
            }
        }
    };

    handleFirstHitHover = () => {
        const {loggedFirstHitHover} = this.state;

        if (!loggedFirstHitHover) {
            edapTrigger(GENERIC_EVENT, {
                eventaction: 'first hit hover',
                highfrequency: 'true'
            });
            this.setState({
                loggedFirstHitHover: true
            });
        }
    };

    checkForMobileScroller = (currentScrollTop: number) => {
        if (
            this.state.scrolled['10'] >= 0 &&
            currentScrollTop >= this.state.scrolled['10']
        ) {
            this.props.showMobileScroller();
        } else if (currentScrollTop < this.state.scrolled['10']) {
            this.props.hideMobileScroller();
        }
    };

    scrollToTopAnimated = () => {
        edapTrigger(GENERIC_EVENT, {
            eventaction: 'back to top click'
        });
        scrollToAnimated(0, 3000, 'easeOutSine', () => {
            this.props.hideMobileScroller();
            this.setState({scrolled: defaultScrolledState});
        });
    };

    render() {
        const {loading, results, numFilters, error} = this.props;

        if (loading) {
            // Loading Hit Placeholders
            return <ResultsLoading numLoadingPlaceHolders={3} />;
        } else if (results) {
            const {
                fromRecord,
                toRecord,
                resultCount,
                page,
                pageCount,
                pageSize
            } = results;
            const pagerData: PagerShape = {
                fromRecord,
                toRecord,
                resultCount,
                page,
                pageCount
            };
            const resultsCount: ResultsCountShape = {
                fromRecord,
                toRecord,
                resultCount
            };
            const transformedResults = filterGraphQlAnywhere(
                hitCollectionFragments.SearchResult,
                results
            );
            const isNoResults = !resultCount || !transformedResults.listings;

            return (
                <div
                    className="Results"
                    role="region"
                    aria-labelledby="resultsCountDisclaimerHeading"
                >
                    <ConnectedSkipToContentAnchor id="skipToSearchResults" />
                    <HitCarouselIconDefinitions />
                    <MetaBar resultsCount={resultsCount} />

                    {(isNoResults && <NoResults numFilters={numFilters} />) || (
                        <HitCollection
                            results={transformedResults}
                            onScrollHitEnter={this.handleScrollHitEnter}
                            onHitMouseEnter={this.onHitMouseEnter}
                            onHitMouseLeave={this.onHitMouseLeave}
                        />
                    )}
                    {!media(this.props.breakpoint, {minWidth: 'SM_MIN'}) && (
                        <BackToTopButton
                            clickHandler={this.scrollToTopAnimated}
                        />
                    )}
                    {!!results.expandedGroups &&
                        !!results.expandedGroups.length && (
                            <ExpandedGroups
                                onHitMouseEnter={this.onHitMouseEnter}
                                onHitMouseLeave={this.onHitMouseLeave}
                                expandedGroups={results.expandedGroups}
                                page={page}
                                pageSize={pageSize}
                            />
                        )}

                    <div data-wdio="ResultsFooter">
                        <ConnectedSkipToContentAnchor id="skipToSearchResultsPagination" />
                        <Unveil threshold={500}>
                            <Pager pager={pagerData} />
                            {!isNoResults && <DisclaimerMessaging ariaHidden />}
                        </Unveil>
                    </div>
                </div>
            );
        }

        const timeoutError =
            error &&
            error.graphQLErrors &&
            error.graphQLErrors.filter(
                ({extensions = {}}) => extensions.code === 'EXTERNAL_TIMEOUT'
            )[0];

        if (timeoutError && timeoutError.extensions) {
            return (
                <ErrorReport
                    name={'search timeout'}
                    logData={{...timeoutError.extensions.request}}
                >
                    <Oops timeout />
                </ErrorReport>
            );
        }

        return (
            <ErrorReport name={OOPS_ERROR_NAME}>
                <Oops />
            </ErrorReport>
        );
    }
}

const mapStateToProps = (state: globalState): StateProps => {
    let {viewport, abacus, system} = state;
    return {
        numFilters: state.searchParams.numFilters,
        breakpoint: viewport.breakpoint,
        abacusExperiments: abacus,
        isDesktop: system.isDesktop
    };
};

const mapDispatchToProps = {
    showMobileScroller: () => displayActions.toggleMobileScroller(true),
    hideMobileScroller: () => displayActions.toggleMobileScroller(false),
    syncInputParams: searchParamsActions.syncInputParams,
    highlightPin: mapActions.highlightPin,
    clearHighlightedPins: mapActions.clearHighlightedPins
};

export default compose(connect(mapStateToProps, mapDispatchToProps))(Results);
